import { useCallback, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CtaBand } from "../../../components/site/CtaBand";
import { Lightbox, type LightboxItem } from "../../../components/site/Lightbox";
import { ProductCard, SiteImage } from "../../../components/site/ProductCard";
import { SiteIcon } from "../../../components/site/SiteIcon";
import { usePageMeta } from "../../../hooks/usePageMeta";
import { PRODUCTS, productBySlug, type Product } from "../../../content/products";
import { BUSINESS, quotePath, SITE_TITLE_SUFFIX } from "../../../content/site";
import { notFoundElement } from "../../../routes/registry";

/** Validation state: a slug is lower-case letters and hyphens only, and must name a published product. */
const SLUG_PATTERN = /^[a-z-]+$/;

/** At most this many "You might also like" products (BR-WEB-023). */
export const RELATED_LIMIT = 3;

/** Resolves a route slug to a published product, or undefined (→ not-found). */
export function resolveProduct(slug: string | undefined): Product | undefined {
  if (!slug || !SLUG_PATTERN.test(slug)) return undefined;
  return productBySlug(slug);
}

/** BR-WEB-023 — up to three other products from the same range, in range order. */
export function relatedProducts(product: Product): Product[] {
  return PRODUCTS.filter((p) => p.range === product.range && p.slug !== product.slug).slice(0, RELATED_LIMIT);
}

/** "{name} | Greatway Patios Toowoomba" — the legacy product-page title shape (BR-WEB-004). */
export function productTitle(product: Product): string {
  return `${product.name}${SITE_TITLE_SUFFIX} Toowoomba`;
}

/** The legacy product pages had no meta description: use the first intro sentence (baseline § 3). */
export function productDescription(product: Product): string {
  const first = product.intro[0] ?? product.tagline;
  return first.split(/(?<=\.)\s/)[0] ?? first;
}

/**
 * Product detail — replaces the 11 legacy /our-products/item/{slug} pages (Story #25441).
 * Copy, sub-sections, highlights and the gallery are the legacy product content, unchanged
 * (BR-WEB-020). Static content only: no network calls.
 */
export default function ProductDetailPage() {
  const { slug } = useParams();
  const product = resolveProduct(slug);
  if (!product) return <>{notFoundElement()}</>;
  return <ProductDetail key={product.slug} product={product} />;
}

function ProductDetail({ product }: { product: Product }) {
  usePageMeta(productTitle(product), productDescription(product));
  const [photo, setPhoto] = useState<number | null>(null);
  const closePhoto = useCallback(() => setPhoto(null), []);
  const photos: LightboxItem[] = useMemo(
    () =>
      product.images.map((src, i) => ({ src, cap: `${product.name} — ${i + 1} of ${product.images.length}` })),
    [product],
  );
  const related = relatedProducts(product);
  const quote = quotePath(product.slug);

  return (
    <>
      <section className="gw-pd-hero" aria-labelledby="pd-title">
        <div className="gw-wrap gw-pd-grid">
          <div>
            <nav className="gw-crumbs" aria-label="Breadcrumb">
              <Link to="/">Home</Link> <span aria-hidden="true">/</span> <Link to="/products">Products</Link>{" "}
              <span aria-hidden="true">/</span> <span aria-current="page">{product.name}</span>
            </nav>
            <p className="gw-eyebrow">{product.category}</p>
            <h1 id="pd-title">{product.name}</h1>
            <p className="tag">{product.tagline}</p>
            <div className="gw-actions">
              <Link className="gw-btn gw-btn-red gw-btn-lg" to={quote}>
                Get a free quote <SiteIcon name="arrow" size={18} />
              </Link>
              <a className="gw-btn gw-btn-line gw-btn-lg" href={BUSINESS.tel}>
                <SiteIcon name="phone" size={18} /> {BUSINESS.phone}
              </a>
            </div>
            <ul className="gw-checks">
              <li>
                <SiteIcon name="check" size={18} /> Free measure and obligation-free quote
              </li>
              <li>
                <SiteIcon name="check" size={18} /> Council permits and requirements handled
              </li>
              <li>
                <SiteIcon name="check" size={18} /> Installed by qualified tradesmen, supervised by our licensed
                builder
              </li>
            </ul>
          </div>
          <div className="gw-pd-media">
            <SiteImage src={product.hero} alt={`${product.name} installed by Greatway Patios`} eager />
          </div>
        </div>
      </section>

      <div className="gw-wrap gw-pd-body">
        <div className="gw-prose" data-testid="product-copy">
          {product.intro.map((t) => (
            <p key={t}>{t}</p>
          ))}
          {product.highlights.length > 0 ? (
            <ul className="gw-hl" data-testid="product-highlights">
              {product.highlights.map((h) => (
                <li key={h}>
                  <SiteIcon name="check" size={18} />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {product.sections.map((s) => (
            <section key={s.heading} aria-label={s.heading}>
              <h2>{s.heading}</h2>
              {s.paras.map((t) => (
                <p key={t}>{t}</p>
              ))}
            </section>
          ))}
        </div>
        <aside className="gw-aside" aria-labelledby="pd-aside">
          <h2 id="pd-aside" className="sr-only">
            Quote and display centre
          </h2>
          <div className="gw-box red">
            <h3>Free measure &amp; quote</h3>
            <p>Tell us about your space and we’ll prepare an obligation-free quote for {product.name}.</p>
            <Link className="gw-btn gw-btn-white" to={quote}>
              Start my quote
            </Link>
          </div>
          <div className="gw-box">
            <h3>See it in person</h3>
            <p>{product.name} is on show at our display centre, with samples and colours to choose from.</p>
            <div className="row">
              <SiteIcon name="pin" size={16} /> {BUSINESS.street}, {BUSINESS.town}
            </div>
            <div className="row">
              <SiteIcon name="clock" size={16} /> {BUSINESS.hours}
            </div>
            <Link className="gw-btn gw-btn-line" to="/contact">
              Plan your visit
            </Link>
          </div>
        </aside>
      </div>

      {photos.length > 0 ? (
        <section className="gw-sec" style={{ paddingTop: 40 }} aria-labelledby="pd-photos">
          <div className="gw-wrap">
            <div className="gw-sechead">
              <div>
                <p className="gw-eyebrow">Recent projects</p>
                <h2 id="pd-photos" className="gw-h2">
                  {product.name} we’ve built
                </h2>
              </div>
              <button type="button" className="gw-btn gw-btn-line" onClick={() => setPhoto(0)}>
                View all photos <span style={{ fontWeight: 400, color: "var(--muted)" }}>({photos.length})</span>
              </button>
            </div>
            <div className="gw-gallery" data-testid="product-gallery">
              {photos.map((p, i) => (
                <button
                  key={`${i}:${p.src}`}
                  type="button"
                  onClick={() => setPhoto(i)}
                  aria-label={`Open photo ${i + 1} of ${photos.length}`}
                >
                  <SiteImage src={p.src} alt={`${product.name} project ${i + 1}`} />
                </button>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {related.length > 0 ? (
        <section className="gw-sec sand tight" aria-labelledby="pd-related">
          <div className="gw-wrap">
            <div className="gw-sechead">
              <div>
                <p className="gw-eyebrow">{product.category}</p>
                <h2 id="pd-related" className="gw-h2">
                  You might also like
                </h2>
              </div>
            </div>
            <div className="gw-grid" data-testid="related-products">
              {related.map((p) => (
                <ProductCard key={p.slug} product={p} variant="tile" />
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <CtaBand title={`Thinking about ${product.name}? Let’s talk.`} product={product.slug} />

      <Lightbox items={photos} index={photo} onClose={closePhoto} onIndexChange={setPhoto} />
    </>
  );
}
